

import * as React from "react"
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react"
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "vtwo:bg-background vtwo:group/calendar vtwo:p-3 vtwo:[--cell-size:--spacing(8)] vtwo:[[data-slot=card-content]_&]:bg-transparent vtwo:[[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("vtwo:w-fit", defaultClassNames.root),
        months: cn(
          "vtwo:flex vtwo:gap-4 vtwo:flex-col vtwo:md:flex-row vtwo:relative",
          defaultClassNames.months
        ),
        month: cn("vtwo:flex vtwo:flex-col vtwo:w-full vtwo:gap-4", defaultClassNames.month),
        nav: cn(
          "vtwo:flex vtwo:items-center vtwo:gap-1 vtwo:w-full vtwo:absolute vtwo:top-0 vtwo:inset-x-0 vtwo:justify-between",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "vtwo:size-(--cell-size) vtwo:aria-disabled:opacity-50 vtwo:p-0 vtwo:select-none",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "vtwo:size-(--cell-size) vtwo:aria-disabled:opacity-50 vtwo:p-0 vtwo:select-none",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "vtwo:flex vtwo:items-center vtwo:justify-center vtwo:h-(--cell-size) vtwo:w-full vtwo:px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "vtwo:w-full vtwo:flex vtwo:items-center vtwo:text-sm vtwo:font-medium vtwo:justify-center vtwo:h-(--cell-size) vtwo:gap-1.5",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "vtwo:relative vtwo:has-focus:border-ring vtwo:border vtwo:border-input vtwo:shadow-xs vtwo:has-focus:ring-ring/50 vtwo:has-focus:ring-[3px] vtwo:rounded-md",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "vtwo:absolute vtwo:bg-popover vtwo:inset-0 vtwo:opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "vtwo:select-none vtwo:font-medium",
          captionLayout === "label"
            ? "vtwo:text-sm"
            : "vtwo:rounded-md vtwo:pl-2 vtwo:pr-1 vtwo:flex vtwo:items-center vtwo:gap-1 vtwo:text-sm vtwo:h-8 vtwo:[&>svg]:text-muted-foreground vtwo:[&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "vtwo:w-full vtwo:border-collapse",
        weekdays: cn("vtwo:flex", defaultClassNames.weekdays),
        weekday: cn(
          "vtwo:text-muted-foreground vtwo:rounded-md vtwo:flex-1 vtwo:font-normal vtwo:text-[0.8rem] vtwo:select-none",
          defaultClassNames.weekday
        ),
        week: cn("vtwo:flex vtwo:w-full vtwo:mt-2", defaultClassNames.week),
        week_number_header: cn(
          "vtwo:select-none vtwo:w-(--cell-size)",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "vtwo:text-[0.8rem] vtwo:select-none vtwo:text-muted-foreground",
          defaultClassNames.week_number
        ),
        day: cn(
          "vtwo:relative vtwo:w-full vtwo:h-full vtwo:p-0 vtwo:text-center vtwo:[&:first-child[data-selected=true]_button]:rounded-l-md vtwo:[&:last-child[data-selected=true]_button]:rounded-r-md vtwo:group/day vtwo:aspect-square vtwo:select-none",
          defaultClassNames.day
        ),
        range_start: cn(
          "vtwo:rounded-l-md vtwo:bg-accent",
          defaultClassNames.range_start
        ),
        range_middle: cn("vtwo:rounded-none", defaultClassNames.range_middle),
        range_end: cn("vtwo:rounded-r-md vtwo:bg-accent", defaultClassNames.range_end),
        today: cn(
          "vtwo:bg-accent vtwo:text-accent-foreground vtwo:rounded-md vtwo:data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "vtwo:text-muted-foreground vtwo:aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "vtwo:text-muted-foreground vtwo:opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("vtwo:invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("vtwo:size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("vtwo:size-4", className)}
                {...props}
              />
            )
          }

          return (
            <ChevronDownIcon className={cn("vtwo:size-4", className)} {...props} />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="vtwo:flex vtwo:size-(--cell-size) vtwo:items-center vtwo:justify-center vtwo:text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "vtwo:data-[selected-single=true]:bg-primary vtwo:data-[selected-single=true]:text-primary-foreground vtwo:data-[range-middle=true]:bg-accent vtwo:data-[range-middle=true]:text-accent-foreground vtwo:data-[range-start=true]:bg-primary vtwo:data-[range-start=true]:text-primary-foreground vtwo:data-[range-end=true]:bg-primary vtwo:data-[range-end=true]:text-primary-foreground vtwo:group-data-[focused=true]/day:border-ring vtwo:group-data-[focused=true]/day:ring-ring/50 vtwo:dark:hover:text-accent-foreground vtwo:flex vtwo:aspect-square vtwo:size-auto vtwo:w-full vtwo:min-w-(--cell-size) vtwo:flex-col vtwo:gap-1 vtwo:leading-none vtwo:font-normal vtwo:group-data-[focused=true]/day:relative vtwo:group-data-[focused=true]/day:z-10 vtwo:group-data-[focused=true]/day:ring-[3px] vtwo:data-[range-end=true]:rounded-md vtwo:data-[range-end=true]:rounded-r-md vtwo:data-[range-middle=true]:rounded-none vtwo:data-[range-start=true]:rounded-md vtwo:data-[range-start=true]:rounded-l-md vtwo:[&>span]:text-xs vtwo:[&>span]:opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
